import { useEffect, useState } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Select,
  DatePicker,
  Input,
  Tag,
  Modal,
  Descriptions,
  Row,
  Col,
  Statistic,
  message,
} from 'antd'
import {
  SearchOutlined,
  ExportOutlined,
  EyeOutlined,
  UserOutlined,
  FileTextOutlined,
  SettingOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  GiftOutlined,
  BarChartOutlined,
  SafetyOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { confirmDanger } from '../../hooks/useConfirm'
import { logAPI } from '../../api'

const { Option } = Select
const { RangePicker } = DatePicker

// 操作类型
type ActionType =
  | 'login' | 'logout'
  | 'create' | 'update' | 'delete'
  | 'export' | 'import'
  | 'enable' | 'disable'
  | 'upload' | 'download'
  | 'view'

// 操作模块
type ModuleType =
  | 'dashboard' | 'products' | 'price' | 'orders'
  | 'members' | 'coupons' | 'statistics'
  | 'settings' | 'system'

// 操作日志接口
interface OperationLog {
  id: number
  operatorId: number
  operatorName: string
  operatorRole: string
  actionType: ActionType
  actionName: string
  moduleType: ModuleType
  moduleName: string
  targetId: number | null
  targetName: string
  detail: string
  ipAddress: string
  userAgent: string
  requestUrl: string
  requestMethod: string
  requestParams: string
  responseStatus: number
  executeTime: number
  createdAt: string
}

// 模块配置
const moduleMap: Record<ModuleType, { name: string; color: string; icon: React.ReactNode }> = {
  dashboard: { name: '仪表盘', color: 'blue', icon: <BarChartOutlined /> },
  products: { name: '商品管理', color: 'green', icon: <ShoppingOutlined /> },
  price: { name: '价格日期', color: 'cyan', icon: <ShoppingOutlined /> },
  orders: { name: '订单管理', color: 'orange', icon: <ShoppingCartOutlined /> },
  members: { name: '会员管理', color: 'purple', icon: <TeamOutlined /> },
  coupons: { name: '优惠券管理', color: 'magenta', icon: <GiftOutlined /> },
  statistics: { name: '数据报表', color: 'gold', icon: <BarChartOutlined /> },
  settings: { name: '系统设置', color: 'default', icon: <SettingOutlined /> },
  system: { name: '系统操作', color: 'red', icon: <SafetyOutlined /> },
}

// 操作类型配置
const actionMap: Record<ActionType, { name: string; color: string }> = {
  login: { name: '登录', color: 'green' },
  logout: { name: '登出', color: 'default' },
  create: { name: '新增', color: 'blue' },
  update: { name: '修改', color: 'orange' },
  delete: { name: '删除', color: 'red' },
  export: { name: '导出', color: 'cyan' },
  import: { name: '导入', color: 'geekblue' },
  enable: { name: '启用', color: 'green' },
  disable: { name: '禁用', color: 'volcano' },
  upload: { name: '上传', color: 'lime' },
  download: { name: '下载', color: 'gold' },
  view: { name: '查看', color: 'default' },
}

const OperationLogs = () => {
  const [loading, setLoading] = useState(false)
  const [logs, setLogs] = useState<OperationLog[]>([])
  const [detailVisible, setDetailVisible] = useState(false)
  const [selectedLog, setSelectedLog] = useState<OperationLog | null>(null)
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs(),
  ])
  const [filterModule, setFilterModule] = useState<ModuleType | 'all'>('all')
  const [filterAction, setFilterAction] = useState<ActionType | 'all'>('all')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 })

  // 统计数据
  const [stats, setStats] = useState({
    totalLogs: 0,
    todayLogs: 0,
    errorLogs: 0,
    avgExecuteTime: 0,
  })

  useEffect(() => {
    fetchLogs()
  }, [dateRange, filterModule, filterAction, searchKeyword, pagination.current, pagination.pageSize])

  const fetchLogs = async () => {
    setLoading(true)
    try {
      const params: any = {
        page: pagination.current,
        pageSize: pagination.pageSize,
        startDate: dateRange[0].format('YYYY-MM-DD HH:mm:ss'),
        endDate: dateRange[1].format('YYYY-MM-DD HH:mm:ss'),
      }
      if (filterModule !== 'all') params.module = filterModule
      if (filterAction !== 'all') params.action = filterAction
      if (searchKeyword) params.keyword = searchKeyword

      const res = await logAPI.getList(params)
      if (res.code === 200) {
        setLogs(res.data.list || [])
        setPagination(prev => ({ ...prev, total: res.data.total || 0 }))
        if (res.data.stats) {
          setStats(res.data.stats)
        }
      }
    } catch (error) {
      // 错误由 request 拦截器统一处理
    } finally {
      setLoading(false)
    }
  }

  const handleViewDetail = async (record: OperationLog) => {
    try {
      const res = await logAPI.getDetail(record.id)
      if (res.code === 200) {
        setSelectedLog(res.data)
      } else {
        setSelectedLog(record)
      }
    } catch (error) {
      setSelectedLog(record)
    }
    setDetailVisible(true)
  }

  const handleExport = async () => {
    try {
      const params = {
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        module: filterModule !== 'all' ? filterModule : undefined,
        action: filterAction !== 'all' ? filterAction : undefined,
        keyword: searchKeyword || undefined,
      }
      const res = await logAPI.export(params)

      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `操作日志_${dayjs().format('YYYYMMDD')}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
      message.success('导出成功')
    } catch (error) {
      message.error('导出失败')
    }
  }

  const handleClearLogs = () => {
    confirmDanger({
      title: '确认清空日志',
      content: '确定要清空所有操作日志吗？此操作不可恢复，请谨慎操作。',
      okText: '确认清空',
      okType: 'danger',
      module: '系统设置',
      action: '清空日志',
      targetName: '操作日志',
      onOk: async () => {
        await logAPI.clear()
        message.success('日志已清空')
        fetchLogs()
      },
    })
  }

  const columns: ColumnsType<OperationLog> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    {
      title: '操作人',
      key: 'operator',
      width: 140,
      render: (_: any, record: OperationLog) => (
        <div>
          <div>{record.operatorName}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.operatorRole}</div>
        </div>
      ),
    },
    {
      title: '操作类型',
      dataIndex: 'actionType',
      key: 'actionType',
      width: 90,
      render: (val: ActionType) => (
        <Tag color={actionMap[val]?.color}>{actionMap[val]?.name}</Tag>
      ),
    },
    {
      title: '模块',
      dataIndex: 'moduleType',
      key: 'moduleType',
      width: 130,
      render: (val: ModuleType) => (
        <Space>
          {moduleMap[val]?.icon}
          <Tag color={moduleMap[val]?.color}>{moduleMap[val]?.name}</Tag>
        </Space>
      ),
    },
    {
      title: '操作对象',
      key: 'target',
      width: 180,
      render: (_: any, record: OperationLog) => (
        <div>
          <div>{record.targetName || '-'}</div>
          {record.targetId && (
            <div style={{ fontSize: 12, color: '#999' }}>ID: {record.targetId}</div>
          )}
        </div>
      ),
    },
    {
      title: '详情',
      dataIndex: 'detail',
      key: 'detail',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'IP地址',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 130,
    },
    {
      title: '状态',
      dataIndex: 'responseStatus',
      key: 'responseStatus',
      width: 70,
      render: (val: number) => (
        <Tag color={val < 400 ? 'green' : 'red'}>{val}</Tag>
      ),
    },
    {
      title: '耗时(ms)',
      dataIndex: 'executeTime',
      key: 'executeTime',
      width: 90,
      render: (val: number) => (
        <span style={{ color: val > 300 ? '#fa8c16' : '#52c41a' }}>{val}</span>
      ),
    },
    {
      title: '操作时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (val: string) => val ? dayjs(val).format('MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 70,
      fixed: 'right' as const,
      render: (_: any, record: OperationLog) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)} />
      ),
    },
  ]

  return (
    <div className="operation-logs-page">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic title="日志总数" value={stats.totalLogs} prefix={<FileTextOutlined />} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="今日操作" value={stats.todayLogs} prefix={<UserOutlined />} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="异常操作" value={stats.errorLogs} prefix={<SafetyOutlined />} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="平均耗时" value={stats.avgExecuteTime} suffix="ms" valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
      </Row>

      <Card size="small">
        <Space wrap style={{ marginBottom: 16 }}>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
          />
          <Select
            style={{ width: 140 }}
            value={filterModule}
            onChange={setFilterModule}
            allowClear
            placeholder="全部模块"
          >
            <Option value="all">全部模块</Option>
            {Object.entries(moduleMap).map(([key, val]) => (
              <Option key={key} value={key}>{val.name}</Option>
            ))}
          </Select>
          <Select
            style={{ width: 120 }}
            value={filterAction}
            onChange={setFilterAction}
            allowClear
            placeholder="全部操作"
          >
            <Option value="all">全部操作</Option>
            {Object.entries(actionMap).map(([key, val]) => (
              <Option key={key} value={key}>{val.name}</Option>
            ))}
          </Select>
          <Input
            placeholder="搜索操作人/对象/详情"
            prefix={<SearchOutlined />}
            style={{ width: 220 }}
            value={searchKeyword}
            onChange={e => setSearchKeyword(e.target.value)}
            onPressEnter={fetchLogs}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={fetchLogs}>
            搜索
          </Button>
          <Button icon={<ExportOutlined />} onClick={handleExport}>
            导出
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={handleClearLogs}>
            清空日志
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1600 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => setPagination({ ...pagination, current: page, pageSize }),
          }}
        />
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="操作详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={700}
      >
        {selectedLog && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="日志ID" span={2}>{selectedLog.id}</Descriptions.Item>
            <Descriptions.Item label="操作人">{selectedLog.operatorName}</Descriptions.Item>
            <Descriptions.Item label="角色">{selectedLog.operatorRole}</Descriptions.Item>
            <Descriptions.Item label="操作类型">
              <Tag color={actionMap[selectedLog.actionType]?.color}>{selectedLog.actionName}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="操作模块">
              <Space>
                {moduleMap[selectedLog.moduleType]?.icon}
                <span>{selectedLog.moduleName}</span>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="操作对象">{selectedLog.targetName || '-'}</Descriptions.Item>
            <Descriptions.Item label="对象ID">{selectedLog.targetId || '-'}</Descriptions.Item>
            <Descriptions.Item label="操作详情" span={2}>{selectedLog.detail}</Descriptions.Item>
            <Descriptions.Item label="IP地址">{selectedLog.ipAddress}</Descriptions.Item>
            <Descriptions.Item label="响应状态">
              <Tag color={selectedLog.responseStatus < 400 ? 'green' : 'red'}>
                {selectedLog.responseStatus}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="执行耗时">{selectedLog.executeTime} ms</Descriptions.Item>
            <Descriptions.Item label="操作时间">{dayjs(selectedLog.createdAt).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
            <Descriptions.Item label="请求方式">{selectedLog.requestMethod}</Descriptions.Item>
            <Descriptions.Item label="请求URL" span={2}>{selectedLog.requestUrl}</Descriptions.Item>
            <Descriptions.Item label="请求参数" span={2}>
              <pre style={{ maxHeight: 200, overflow: 'auto', fontSize: 12 }}>
                {selectedLog.requestParams ? JSON.stringify(JSON.parse(selectedLog.requestParams), null, 2) : '-'}
              </pre>
            </Descriptions.Item>
            <Descriptions.Item label="User-Agent" span={2}>{selectedLog.userAgent}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  )
}

export default OperationLogs
